/**
 * Этап 5 / KS-02 — contacts as a DIRECTORY, and the Clients section over it.
 *
 * ── The problem this contract exists to remove.
 * Ксения created a project and found that a hiring manager already in the system was
 * not offered for selection — the contact had to be typed in again. Two mechanisms
 * were behind that: `projects.contact_id` allows exactly ONE contact per project, and
 * every additional addressee lived in `projects.data.cc_emails` as a flat string array
 * scraped out of an inbound email header. Strings cannot be selected, reused or
 * corrected. EER's real model is "several contacts on one project, all of them need
 * CC" (ADR 2026-07-29 «KS-02»), which neither mechanism expressed.
 *
 * ── Why the picker is a LIST and not a type-ahead.
 * PM's objection, and it decides the shape of these DTOs: **EER users do not remember
 * what a client's contact is called.** They remember the company. So the client is
 * chosen by company name, and the client's contacts then arrive as a complete list —
 * `clientContactsResponseSchema` returns ALL of them, unpaged and unsearched, because
 * the user is recognising a name, not recalling it.
 *
 * ── Why merge is part of the contract and not a later nicety.
 * Contacts are auto-extracted from inbound email by an LLM, so duplicates are
 * inevitable — the live database already carries `adam@eerglobal.pl` twice. Without a
 * merge path the directory degrades into the same "type it again" experience it was
 * built to fix. Merge is destructive, so it is confirm-gated and audited (ADR «KS-02»
 * последний пункт Consequences).
 *
 * ── Why every `success` below is `.optional()` — do NOT tighten it back.
 * The backend always sends `success: true`. The WEB transport then removes it:
 * `toApiResult` → `unwrapLegacySuccess` strips the flag from any successful object
 * response (legacy envelope parity, `shared/legacy.ts`). So a response schema that
 * REQUIRES the flag can never validate in the browser, while passing every server-side
 * test — which is exactly how the whole Clients section shipped broken and was caught
 * only by the e2e run (`invalid_literal … expected true` rendered in place of the
 * list). Same convention and comment as `admin-prompts.ts` / `admin-questionnaire.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
/**
 * How a contact participates in a project.
 *
 * `primary` is the main addressee — the value `projects.contact_id` used to hold, and
 * there is at most ONE per project (enforced by a partial unique index, not by
 * callers). `cc` is an addressee in copy, and there may be any number.
 */
export const projectContactRoleSchema = z.enum(['primary', 'cc']);
// ---------------------------------------------------------------------------
// Read DTOs
// ---------------------------------------------------------------------------
/**
 * One contact as the directory shows it.
 *
 * `client_id` is NULLABLE and stays that way: a contact extracted from an inbound
 * email must persist even when no client could be resolved (E02-F02-T06) — the system
 * never fabricates an "Unknown Client" row. The Clients section therefore needs a
 * place for unattached contacts, which is why this DTO does not assume a client.
 */
export const contactSchema = z.object({
    id: z.number().int(),
    client_id: z.number().int().nullable(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    position: z.string().nullable(),
    notes: z.string().nullable(),
    /** How many projects reference this contact (any role). Drives "safe to delete?". */
    projects_count: z.number().int().nonnegative(),
});
/** One client row in the Clients list. */
export const clientSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    domain: z.string().nullable(),
    website: z.string().nullable(),
    industry: z.string().nullable(),
    notes: z.string().nullable(),
    contacts_count: z.number().int().nonnegative(),
    projects_count: z.number().int().nonnegative(),
});
/** A project as it appears under a client or a contact (enough to navigate to it). */
export const linkedProjectSchema = z.object({
    id: z.number().int(),
    role_name: z.string(),
    stage: z.number().int().nullable(),
    status: z.string().nullable(),
    /** Present when the project is reached through a contact. */
    contact_role: projectContactRoleSchema.optional(),
});
/** GET /clients — the directory list, ordered by name. */
export const clientsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    clients: z.array(clientSchema),
    /** Contacts with `client_id = null`; they have nowhere else to appear. */
    unassigned_contacts: z.array(contactSchema),
});
/** GET /clients/:id — one client with its contacts and projects. */
export const clientDetailResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    client: clientSchema,
    contacts: z.array(contactSchema),
    projects: z.array(linkedProjectSchema),
});
/**
 * GET /clients/:id/contacts — the picker's data source.
 *
 * Complete and unpaged on purpose: the user is recognising a name in a list, and a
 * "load more" would defeat that. A client with an unmanageable number of contacts is
 * not a case EER has (the live directory holds 14 contacts in total).
 */
export const clientContactsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    contacts: z.array(contactSchema),
});
/** GET /contacts/:id — one contact with the projects it takes part in. */
export const contactDetailResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    contact: contactSchema,
    projects: z.array(linkedProjectSchema),
});
// ---------------------------------------------------------------------------
// Project ↔ contacts
// ---------------------------------------------------------------------------
/** GET /projects/:id/contacts — the authoritative addressee list of one project. */
export const projectContactsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /** Null when the project has no main addressee yet (a legitimate state). */
    primary: contactSchema.nullable(),
    cc: z.array(contactSchema),
});
/**
 * PUT /projects/:id/contacts — replaces the whole set.
 *
 * A full replacement rather than add/remove verbs: the picker is a form the user
 * submits, and two round-trips ("remove B, add C") can leave the project with no
 * primary between them, which is a state a send could observe.
 */
export const setProjectContactsSchema = z.object({
    /** Null clears the main addressee. */
    primary_contact_id: z.number().int().positive().nullable(),
    /** Contacts in copy. The primary is removed from this list server-side, not rejected:
     *  ticking the main addressee "also in copy" is a mistake, not an error worth a 400. */
    cc_contact_ids: z.array(z.number().int().positive()).default([]),
});
// ---------------------------------------------------------------------------
// Writes — clients and contacts
// ---------------------------------------------------------------------------
/** Trimmed non-empty string; an all-whitespace name is a validation error, not a row. */
const requiredName = z.string().trim().min(1, 'Name is required').max(255);
const optionalText = z.string().trim().max(255).nullish();
export const clientCreateSchema = z.object({
    name: requiredName,
    domain: optionalText,
    website: optionalText,
    industry: optionalText,
    notes: z.string().nullish(),
});
/** PATCH — every field optional, but a supplied `name` must still be non-empty. */
export const clientPatchSchema = clientCreateSchema.partial();
// Named `contactCreateSchema`/`contactPatchSchema`, not `create/updateContactSchema`:
// `updateContactSchema` is already taken by `projectMutations.ts` for a DIFFERENT thing
// (the `updates.contact` sub-object of PATCH /projects/:id). The shared barrel is a flat
// `export *`, so a same-name export there is a build error — and, worse, a reader could
// import the wrong one and silently accept the wrong fields.
export const contactCreateSchema = z.object({
    /** Null is allowed: an extracted contact may have no resolvable client. */
    client_id: z.number().int().positive().nullable().optional(),
    name: requiredName,
    /** Format is NOT enforced: legacy rows carry whatever the LLM extracted, and
     *  rejecting them here would make an existing contact uneditable. */
    email: optionalText,
    phone: z.string().trim().max(50).nullish(),
    position: optionalText,
    notes: z.string().nullish(),
});
export const contactPatchSchema = contactCreateSchema.partial();
export const clientMutationResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    client: clientSchema,
});
export const contactMutationResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    contact: contactSchema,
});
export const directoryOkResponseSchema = z.object({ success: z.literal(true).optional() });
// ---------------------------------------------------------------------------
// Duplicates and merge
// ---------------------------------------------------------------------------
/**
 * One suspected duplicate group.
 *
 * `reason` says WHY the group was proposed, because the two reasons carry very
 * different confidence: an identical email is all but proof, while a matching name is
 * a hint (EER has several clients whose names are people's names). The UI shows the
 * reason so the human merging is not guessing.
 */
/**
 * One row inside a duplicate group, described well enough to choose the survivor
 * (E12-F17-T08: a bare `#id` told the human nothing about what was being merged).
 *   · clients  — `name` + `domain`; `email`/`client_name` are null.
 *   · contacts — `name` + `email` + `client_name` (the owning client, if any); `domain` null.
 */
export const duplicateMemberSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    domain: z.string().nullable(),
    email: z.string().nullable(),
    client_name: z.string().nullable(),
});
export const duplicateGroupSchema = z.object({
    reason: z.enum(['same_email', 'same_name']),
    /** The normalized value the group matched on — shown as the group heading. */
    value: z.string(),
    ids: z.array(z.number().int()).min(2),
    /** Same rows as `ids`, in the same order. Defaulted so an older payload still parses. */
    members: z.array(duplicateMemberSchema).default([]),
});
export const duplicatesResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    clients: z.array(duplicateGroupSchema),
    contacts: z.array(duplicateGroupSchema),
});
/**
 * Merge N rows into one.
 *
 * `confirm` is required and defaults to FALSE: merge deletes rows and rewrites
 * references, and the system's convention for a destructive step is an explicit second
 * call (the 412 CONFIRM_REQUIRED pattern, ADR 2026-07-20 «PK-QA-1»). The first call
 * answers "what will this do"; the second does it.
 */
export const mergeRequestSchema = z.object({
    /** Rows to absorb. Must not contain `target_id`. */
    source_ids: z.array(z.number().int().positive()).min(1),
    confirm: z.boolean().default(false),
});
/** What a merge did (or, when unconfirmed, would do). */
export const mergePreviewSchema = z.object({
    target_id: z.number().int(),
    source_ids: z.array(z.number().int()),
    /** Projects whose references move to the target. */
    projects_relinked: z.number().int().nonnegative(),
    /** Contacts reassigned to the target client (client merge only). */
    contacts_moved: z.number().int().nonnegative(),
    /** Fields the target gains because it was empty and a source had a value. */
    fields_filled: z.array(z.string()),
});
export const mergeResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /** False when `confirm` was not set — nothing was written. */
    merged: z.boolean(),
    preview: mergePreviewSchema,
});
//# sourceMappingURL=clients.js.map